import React, { useRef, useEffect } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { Button } from 'primereact/button'
import { Messages } from 'primereact/messages'

import { AlertIcon } from '../lib/icons/alert-icon'
import { isAdminDomain, isPartnerDomain, isSupportDomain, isUserappDomain } from '../lib/currentDomain'
import useText from '../common/hooks/useText'
import isAura from '../lib/isAura'
import { theme } from '../lib/theme'
import isHalo from '../lib/isHalo'
import isDawn from '../lib/isDawn'
import isLuna from '../lib/isLuna'
import { ChargePoint, SmartChargingSchedule, SyncStatuses } from '../common/redux/api/web-api'
import { Dropdown } from '../common/uikit/Dropdown'
import timeZones from '../lib/timeZones'

import Text from './Text'
import ConnectorSelect, { ConnectorType, IConnectorId } from './ConnectorSelect'
import CurrentSelect from './CurrentSelect'

const rotationKeyframes = keyframes`
    100% {
        transform:rotate(360deg);
    }
`

const SyncStatusIcon = styled.img<{ rotating?: boolean }>`
    ${(p) =>
        p.rotating &&
        css`
            animation: ${rotationKeyframes} 4s linear infinite;
        `}
    width: 2em;
    margin-right: 0.5rem;
`

const fadeOutKeyframes = keyframes`
    0% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
`

const FadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    animation: ${fadeOutKeyframes} 2s forwards ease 5s;
`

const SyncedStatus = ({ syncStatus }: { syncStatus: SyncStatuses }) => {
    if (syncStatus === 'Synced') {
        return (
            <FadingWrapper>
                <SyncStatusIcon src={'/common/content/images/synced.png'} />
                Synced
            </FadingWrapper>
        )
    }

    return null
}

const SyncStatus = ({ syncStatus }: { syncStatus: SyncStatuses }) => {
    if (syncStatus === 'Syncing') {
        return (
            <span title="It take times to apply changes to charger. Please make sure your charger is online if charger is not charging according to latest schedule.">
                <SyncStatusIcon src={'/common/content/images/syncing.png'} rotating />
                Syncing
            </span>
        )
    }

    if (syncStatus === 'NotSynced') {
        return (
            <span title="It take times to apply changes to charger. Please make sure your charger is online if charger is not charging according to latest schedule.">
                <SyncStatusIcon src={'/common/content/images/not-synced.png'} />
                Not synced
            </span>
        )
    }

    return null
}

export default function ChargePointSchedulesControls({
    chargePoint,
    schedule,
    onUpdateSchedule,
    numberOfConnectors,
    connectorId,
    onChangeConnectorId,
    onClearSchedule,
    minPossibleCurrent,
    maxPossibleCurrent,
    onSaveSchedule,
    disabledSave,
    disabledDefaultCurrent,
    disabledControls,
    disabledClear,
    disabledClearReason,
    syncStatus = 'Synced',
    showSyncedStatusIcon,
}: Props) {
    const text = useText()

    const msgs = useRef<Messages>(null)

    const getConnectorWarningText = () => {
        if (!schedule?.isActive || (schedule?.isActive && schedule?.schedulePeriods?.length === 0)) {
            return null
        }

        const connectorMode = chargePoint?.connectors?.find((c) => c.connectorId === connectorId)?.mode
        if (connectorMode === 'Off') {
            return text('msgScheduleIsIgnoredChargerIsSetToOff')
        } else if (connectorMode === 'On') {
            return text('msgScheduleIsIgnoreChargerIsSetToAlwaysOn')
        }

        return null
    }

    const connectorWarningText = getConnectorWarningText()

    useEffect(() => {
        if (msgs.current) {
            msgs.current.clear()

            if (connectorWarningText) {
                msgs.current.show([
                    {
                        sticky: true,
                        severity: 'warn',
                        detail: connectorWarningText,
                        closable: false,
                    },
                ])
            }
        }
    }, [connectorWarningText, text])

    if (!chargePoint) return null

    const isSchukodisabledforAdmin = isHalo(chargePoint) && connectorId === ConnectorType.CHARGER && isAdminDomain
    const isDefaultCurrentEnabled =
        isSupportDomain || isPartnerDomain || isAura(chargePoint) || isDawn(chargePoint) || isLuna(chargePoint)
    const showMaximumCurrent = isAura(chargePoint) || connectorId === ConnectorType.CHARGER

    return (
        <div className="ml-6">
            <div>
                {isUserappDomain && typeof schedule.defaultCurrent !== 'undefined' && schedule.defaultCurrent > 0 ? (
                    <span className="notification-class ">
                        <span className="alert-icon">
                            <AlertIcon size={28} fill={theme.brandColors.CA_Black} />
                        </span>
                        <span className="mt-2">
                            <Text
                                code="msgUserSmartSchedulingNotifications"
                                values={{ default: Math.round(schedule.defaultCurrent) }}
                            ></Text>
                        </span>
                    </span>
                ) : null}

                <Messages ref={msgs} />

                <div className="grid">
                    {numberOfConnectors > 1 && (
                        <div className="col-12">
                            <ConnectorSelect
                                connectors={chargePoint.connectors ?? []}
                                value={connectorId}
                                onChange={onChangeConnectorId}
                            />
                        </div>
                    )}

                    {(isSchukodisabledforAdmin || isDefaultCurrentEnabled) && !isUserappDomain && (
                        <div className="col-12 md:col-6">
                            <CurrentSelect
                                label={text('msgSetFollowingCurrentForUnscheduledSlots')}
                                infoText={!isUserappDomain && text('msgSmartScheduleChargerUseInstructionshort')}
                                value={schedule.defaultCurrent}
                                onChange={(event) => onUpdateSchedule({ ...schedule, defaultCurrent: event.value })}
                                minValue={minPossibleCurrent}
                                maxValue={maxPossibleCurrent}
                                placeholder={text('lblSelectOption')}
                                disabled={disabledControls || disabledDefaultCurrent}
                                isSocket={!isAura(chargePoint) && connectorId === ConnectorType.SOCKET}
                                error={(() => {
                                    if (schedule.defaultCurrent && schedule.defaultCurrent > maxPossibleCurrent) {
                                        return connectorId === ConnectorType.CHARGER || isAura(chargePoint)
                                            ? text('msgMaximumCurrentReached', { maxPossibleCurrent })
                                            : `Maximum current is set to ${maxPossibleCurrent} A. Socket might not be charging optimally. To ensure optimal charging for socket, set current to 10 A and above.`
                                    }
                                })()}
                            />
                        </div>
                    )}

                    <div className="col-12 md:col-6">
                        <Dropdown
                            label={text('lblSelectTimeZone')}
                            infoText={text('msgTimeZoneShouldHaveSameLocationAsCharger')}
                            disabled={disabledControls}
                            value={schedule.timeZone}
                            onChange={(event) => {
                                onUpdateSchedule({ ...schedule, timeZone: event.value })
                            }}
                            options={timeZones.map((tz) => ({
                                label: `(GMT${tz.utc}) ${tz.name}`,
                                value: tz.code,
                            }))}
                            error={schedule.timeZone ? undefined : text('errTimeZoneRequired')}
                            id="time-zone-select"
                        />
                    </div>
                </div>
            </div>
            <SyncStatus syncStatus={syncStatus} />
            {showSyncedStatusIcon && <SyncedStatus syncStatus={syncStatus} />}
            <div className="flex flex-column md:flex-row md:align-items-end justify-content-between gap-2 mt-2 mb-2">
                <p className="text-sm text-color-secondary m-0">
                    {showMaximumCurrent ? (
                        text('msgMaximumChargingCurrentScheduleLimit', {
                            maximumCurrent: maxPossibleCurrent,
                        })
                    ) : (
                        <Text code="msgSmartScheduleSocketUseInstruction" />
                    )}
                </p>
                <div className="flex justify-content-end gap-2 align-self-end md:align-self-auto">
                    <Button
                        label={text('btnClearSchedule')}
                        icon="pi pi-trash"
                        severity="danger"
                        outlined
                        disabled={disabledClear}
                        tooltip={disabledClearReason}
                        tooltipOptions={{ position: 'top', showOnDisabled: true }}
                        onClick={() => onClearSchedule()}
                    />
                    <Button
                        label={text('btnSaveSchedule')}
                        icon="pi pi-save"
                        onClick={onSaveSchedule}
                        disabled={!!disabledSave || disabledControls}
                        tooltip={typeof disabledSave === 'string' ? disabledSave : undefined}
                        tooltipOptions={{ position: 'top', showOnDisabled: true }}
                    />
                </div>
            </div>
        </div>
    )
}

export type Props = {
    chargePoint?: ChargePoint
    schedule: SmartChargingSchedule
    onUpdateSchedule: (schedule: SmartChargingSchedule) => void
    numberOfConnectors: number
    connectorId: IConnectorId
    onChangeConnectorId: (connectorId: IConnectorId) => void
    onClearSchedule: () => void
    minPossibleCurrent: number
    maxPossibleCurrent: number
    visibleTimeZoneSelector?: boolean
    onSaveSchedule: (event: React.MouseEvent<HTMLButtonElement>) => void
    disabledSave?: boolean | string
    disabledDefaultCurrent?: boolean
    disabledControls?: boolean
    disabledClear: boolean
    disabledClearReason?: string
    syncStatus?: SyncStatuses
    showSyncedStatusIcon: boolean
}
